import { getRetailerToken } from "@faire/help-center-utils/serialized-data/getRetailer";
import { StrictLocalizeFunction } from "@faire/web--source/common/localization";
import { path as getSelfServeRecommendedArticleRoute } from "@faire/web-api--source/routes/www/support/self-serve/recommended-article";
import * as H from "history";
import { useHistory } from "react-router-dom";

import { FaireDirectDiscountsDataFn } from "../instantAnswerDataFunctions/faireDirectDiscountsDataFn";
import { ShippingContextDataFn } from "../instantAnswerDataFunctions/shippingContextDataFn";
import { ShouldDisplayMessageCTADataFn } from "../instantAnswerDataFunctions/shouldDisplayMessageCTADataFn";
import {
  CtaBuilderFn,
  InstantAnswerContext,
  InstantAnswerRequiredDataFn,
} from "../types";

export const NEED_MORE_INFO = (strictLocalize: StrictLocalizeFunction) =>
  strictLocalize({
    defaultMessage: "We might need more info to help solve your issue",
    description: {
      text: "General title for an answer of any issue when no order is selected, or brand order or shipping context is not successfully fetched",
    },
  });

const isBrand = (): boolean => {
  const retailerToken = getRetailerToken();
  if (retailerToken) {
    return false;
  }
  return true;
};

const GENERAL_MESSAGE_RETAILER_OR_BRAND = (
  strictLocalize: StrictLocalizeFunction
) => {
  if (isBrand()) {
    return strictLocalize({
      defaultMessage:
        "For a faster resolution, reach out to the customer first",
      description: {
        text: "General title for an answer of any issue where there is no info available",
      },
    });
  }
  return strictLocalize({
    defaultMessage: "For a faster resolution, reach out to the brand first",
    description: {
      text: "General title for an answer of any issue where there is no info available",
    },
  });
};

const GENERAL_MESSAGE_RETAILER_OR_BRAND_BUTTON = (
  history: H.History,
  strictLocalize: StrictLocalizeFunction
) => {
  if (isBrand()) {
    return {
      label: strictLocalize({
        defaultMessage: "Message the retailer",
        description: {
          text: "CTA button message retailer",
        },
      }),
      onClick: () => messageRetailerOrBrandOnClick(history),
    };
  }
  return {
    label: strictLocalize({
      defaultMessage: "Message the brand",
      description: {
        text: "CTA button message brand",
      },
    }),
    onClick: () => messageRetailerOrBrandOnClick(history),
  };
};

const GENERAL_MESSAGE_DESCRIPTION_RETAILER_OR_BRAND = (
  strictLocalize: StrictLocalizeFunction
) => {
  if (isBrand()) {
    return strictLocalize({
      defaultMessage:
        "Most often, this type of issue can be resolved by contacting the customer. If they aren’t able to help, you can always come back and contact us.",
      description: {
        text: "General description for an answer of any issue where there is no info available",
      },
    });
  }
  return strictLocalize({
    defaultMessage:
      "Most often, this type of issue can be resolved by contacting the brand. If they aren’t able to help, you can always come back and contact us.",
    description: {
      text: "General description for an answer of any issue where there is no info available",
    },
  });
};

export const SELF_SERVE_GENERAL_DESCRIPTION_WITH_CONTEXT = (
  strictLocalize: StrictLocalizeFunction,
  context: InstantAnswerContext
) => {
  if (context.brandOrder) {
    return isBrand()
      ? strictLocalize({
          defaultMessage:
            "Most often, this type of issue can be resolved by contacting the customer. If they aren’t able to help, you can always come back and contact us.",
          description: {
            text: "General description for an answer of any issue where there is no info available",
          },
        })
      : strictLocalize({
          defaultMessage:
            "Most often, this type of issue can be resolved by contacting the brand. If they aren’t able to help, you can always come back and contact us.",
          description: {
            text: "General description for an answer of any issue where there is no info available",
          },
        });
  }
  return GENERAL_MESSAGE_DESCRIPTION_RETAILER_OR_BRAND(strictLocalize);
};

export const SELF_SERVE_GENERAL_TITLE_WITH_CONTEXT = (
  strictLocalize: StrictLocalizeFunction,
  context: InstantAnswerContext
) => {
  if (context.brandOrder) {
    const selectedBrandOrder = context.brandOrder;
    const brandName = selectedBrandOrder?.brand_name;
    const retailerName = selectedBrandOrder?.retailer_name;
    const conversationName = isBrand() ? retailerName : brandName;
    if (!conversationName) {
      return GENERAL_MESSAGE_RETAILER_OR_BRAND(strictLocalize);
    }
    return strictLocalize(
      {
        defaultMessage:
          "For a faster resolution, reach out to {conversationCompanyName} first",
        description: {
          text: "General title for an answer of any issue where there is no info available",
        },
      },
      {
        conversationCompanyName: conversationName,
      }
    );
  }
  return GENERAL_MESSAGE_RETAILER_OR_BRAND(strictLocalize);
};

export const MessageRetailerOrBrandButton: CtaBuilderFn = (
  context: InstantAnswerContext,
  strictLocalize: StrictLocalizeFunction
) => {
  const history = useHistory();
  const brandOrder = context.brandOrder;
  if (!brandOrder || !context.shouldDisplayCTAOfContactBrandOrRetailer) {
    return undefined;
  }
  const selectedBrandOrder = context.brandOrder;
  const brandName = selectedBrandOrder?.brand_name;
  const retailerName = selectedBrandOrder?.retailer_name;
  const conversationName = isBrand() ? retailerName : brandName;

  if (!conversationName) {
    return GENERAL_MESSAGE_RETAILER_OR_BRAND_BUTTON(history, strictLocalize);
  }
  return {
    label: strictLocalize(
      {
        defaultMessage: "Message {conversationCompanyName}",
        description: {
          text: "CTA button message retailer or brand",
        },
      },
      {
        conversationCompanyName: conversationName,
      }
    ),
    onClick: () => messageRetailerOrBrandOnClick(history),
  };
};

const messageRetailerOrBrandOnClick = (history?: H.History) => {
  if (history) {
    history.push(getSelfServeRecommendedArticleRoute({ message_modal: "1" }));
  }
};

export const additionalDataRequiredForContactingRetailerOrBrandCTA =
  (): InstantAnswerRequiredDataFn[] => {
    return [ShippingContextDataFn, ShouldDisplayMessageCTADataFn];
  };

export const additionalDataRequiredForFaireDiscountsSignUpOfferIA =
  (): InstantAnswerRequiredDataFn[] => {
    return [FaireDirectDiscountsDataFn];
  };
